import { FC, useEffect, useState, useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import GameDetails from './GameDetails';
import DealDetails from './DealDetails';
import { useDispatch } from 'react-redux';
import { setToastMessage } from '@stores/App/slice';
import { TOAST_TYPES } from '@constants/constants';
import BankDetails from './BankDetails';
import { useLazyGetDealsQuery } from '@api/deals';
import DealSetupSidePanel from './components/DealSetupSidePanel/DealSetupSidePanel';
import DealUpdatedDetails from './components/DealUpdatedDetails';
import ReferrerDetails from './ReferralDetails';
import { useLazyGetGameByIdQuery } from '@api/games';
import DataDetails from './components/DataDetails';

export interface DealData {
  gameDetailsRow: {
    gameId: string;
    gameName: string;
    companyName: string;
    signingAuthorityName: string;
    signingAuthorityEmail: string;
  };
  investorRows: Array<{
    id: number;
    investorId: string;
    investorName: string;
    investorContribution: string;
    investorReturnCap: string;
  }>;
  interestRow: {
    interestOnUnderspendAmount: string;
  };
  pricingMethodology: {
    interestPercentage: string;
    firstMonthCap: string;
    incrementalReturnCap: string;
    playsomePricing: string;
  };
  validityRows: {
    startDate: string;
    endDate: string;
  };
  bankDetailsRow: {
    bankName: string;
    bankAddress: string;
    swiftCode: string;
    iban: string;
    accountNumber: string;
    routingNumber?: string;
  };
}
const enum DealSetupTabs {
  GAME_DETAILS = 'Game Details',
  DEAL_DETAILS = 'Deal Details',
  BANK_DETAILS = 'Bank Details',
  DATA = 'Data',
  REFERER = 'Referrer',
}

const DealSetupNewDeal: FC<{
  selectedGameId?: string;
  dealData?: DealData;
  handleNewDealCancel: () => void;
  handleEditDealCancel: () => void;
}> = ({ dealData, handleNewDealCancel, handleEditDealCancel, selectedGameId }) => {
  const dispatch = useDispatch();

  const [showDealSetupPanel, setShowDealSetupPanel] = useState(false);
  const [selectedDealSetupTab, setSelectedDealSetupTab] = useState<DealSetupTabs>(
    DealSetupTabs.GAME_DETAILS,
  );

  const [logs, setLogs] = useState<any[]>([]);
  const [gameId, setGameId] = useState<string>(dealData?.gameDetailsRow?.gameId || '');

  const [getDeals] = useLazyGetDealsQuery();
  const [getGameById, { data: gameDetails }] = useLazyGetGameByIdQuery();

  const dealSetupTabKeys = useMemo(
    () => [
      DealSetupTabs.GAME_DETAILS,
      DealSetupTabs.DEAL_DETAILS,
      DealSetupTabs.BANK_DETAILS,
      DealSetupTabs.DATA,
      ...(gameDetails?.data?.status === 'FUNDED' ? [DealSetupTabs.REFERER] : []),
    ],
    [gameDetails?.data?.status],
  );

  useEffect(() => {
    if (selectedGameId) {
      setGameId(selectedGameId);
    }
  }, [selectedGameId]);

  useEffect(() => {
    if (gameId) {
      fetchDealHistory(gameId);
      getGameById(gameId);
    }
  }, [gameId]);

  const handleCancelClick = () => {
    if (gameId) {
      handleEditDealCancel();
    } else {
      handleNewDealCancel();
    }
  };

  const fetchDealHistory = async (gameId: string) => {
    if (!gameId) return;

    try {
      const { data } = await getDeals({ gameId }).unwrap();
      if (data) {
        setLogs(data);
      }
    } catch (error: any) {
      dispatch(
        setToastMessage({
          type: TOAST_TYPES.ERROR,
          message: error?.error?.message,
        }),
      );
    }
  };

  const getTabContent = (tab: DealSetupTabs) => {
    switch (tab) {
      case DealSetupTabs.GAME_DETAILS:
        return (
          <GameDetails
            handleCancelClick={handleCancelClick}
            setGameId={setGameId}
            gameId={gameId}
            dealUpdatedDetailsComponent={
              <DealUpdatedDetails
                openHistoryHandler={() => setShowDealSetupPanel(true)}
                logs={logs}
              />
            }
          />
        );
      case DealSetupTabs.DEAL_DETAILS:
        return (
          <DealDetails
            handleCancelClick={handleCancelClick}
            gameId={gameId}
            dealUpdatedDetailsComponent={
              <DealUpdatedDetails
                openHistoryHandler={() => setShowDealSetupPanel(true)}
                logs={logs}
              />
            }
            fetchDealHistory={fetchDealHistory}
          />
        );
      case DealSetupTabs.BANK_DETAILS:
        return (
          <BankDetails
            handleCancelClick={handleCancelClick}
            gameId={gameId}
            dealUpdatedDetailsComponent={
              <DealUpdatedDetails
                openHistoryHandler={() => setShowDealSetupPanel(true)}
                logs={logs}
              />
            }
          />
        );
      case DealSetupTabs.DATA:
        return (
          <DataDetails
            handleCancelClick={handleCancelClick}
            gameId={gameId}
            dealUpdatedDetailsComponent={
              <DealUpdatedDetails
                openHistoryHandler={() => setShowDealSetupPanel(true)}
                logs={logs}
              />
            }
          />
        );
      case DealSetupTabs.REFERER:
        return (
          <ReferrerDetails
            handleCancelClick={handleCancelClick}
            gameId={gameId}
            dealUpdatedDetailsComponent={
              <DealUpdatedDetails
                openHistoryHandler={() => setShowDealSetupPanel(true)}
                logs={logs}
              />
            }
          />
        );
      default:
        return null;
    }
  };

  return (
    <Box
      sx={{
        width: '100%',
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        border: 'none',
        paddingLeft: '2rem',
        paddingRight: '2rem',
        paddingTop: '1rem',
        paddingBottom: '1rem',
        position: 'absolute',
        top: 0,
        left: 0,
        backgroundColor: '#f5f5f5',
        zIndex: 1,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          gap: '0.5rem',
          mt: '0.5rem',
          mb: '1.5rem',
        }}
      >
        <Box
          sx={{
            cursor: 'pointer',
          }}
          onClick={() => {
            handleCancelClick();
          }}
        >
          <Typography fontSize={'1rem'} fontWeight={500} sx={{ color: '#17191C59' }}>
            Deal Setup
          </Typography>
        </Box>
        <Typography fontSize="1rem" fontWeight={500} sx={{ color: '#1B1C17' }}>
          &gt; {gameId ? gameId : 'New Deal'}
        </Typography>
      </Box>
      <Box
        sx={{
          display: 'flex',
          borderBottom: '1px solid #2F736E1F',
          marginBottom: '1.25rem',
        }}
      >
        {dealSetupTabKeys.map((tab) => (
          <Box
            key={tab}
            onClick={() => {
              if (!gameId) return;
              setSelectedDealSetupTab(tab);
            }}
            sx={{
              padding: '0.75rem 0.875rem',
              cursor: gameId ? 'pointer' : 'default',
              borderBottom: selectedDealSetupTab === tab ? '1px solid #51B8B0' : '',
              transition: 'transform 0.5s ease',
            }}
          >
            <Typography
              fontSize={'1rem'}
              fontWeight={700}
              sx={{
                color: selectedDealSetupTab === tab ? '#51B8B0' : '#6C6C6C',
              }}
            >
              {tab}
            </Typography>
          </Box>
        ))}
      </Box>
      <Box sx={{ flex: 1, overflow: 'hidden' }}>{getTabContent(selectedDealSetupTab)}</Box>
      <DealSetupSidePanel
        showDealSetupPanel={showDealSetupPanel}
        setShowDealSetupPanel={setShowDealSetupPanel}
        deals={logs}
      />
    </Box>
  );
};

export default DealSetupNewDeal;
